import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { MawLogoLogin } from '../shared/MawLogo';
import { auth, db } from '../../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

function firebaseError(code) {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'E-mail ou senha incorretos.';
    case 'auth/email-already-in-use':
      return 'Este e-mail já está cadastrado. Faça login.';
    case 'auth/invalid-email':
      return 'E-mail inválido.';
    case 'auth/weak-password':
      return 'Senha fraca. Use ao menos 6 caracteres.';
    case 'auth/too-many-requests':
      return 'Muitas tentativas. Aguarde alguns minutos.';
    case 'auth/network-request-failed':
      return 'Sem conexão com a internet.';
    default:
      return 'Erro inesperado. Tente novamente.';
  }
}

function resetError(code) {
  if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
    return 'Não encontramos nenhuma conta com esse e-mail.';
  }
  return firebaseError(code);
}

export default function Login() {
  const { state } = useApp();
  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const formatPhone = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  const login = async () => {
    setError('');
    if (!form.email || !form.password) { setError('Preencha todos os campos.'); return; }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, form.email.trim(), form.password);
      // onAuthStateChanged in App.js handles the rest
    } catch (e) {
      setError(firebaseError(e.code));
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async () => {
    setError('');
    if (!resetEmail.trim()) { setError('Digite seu e-mail.'); return; }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setResetSent(true);
    } catch (e) {
      setError(resetError(e.code));
    } finally {
      setLoading(false);
    }
  };

  const register = async () => {
    setError('');
    if (!form.name.trim()) { setError('Digite seu nome.'); return; }
    if (!form.email) { setError('Digite seu e-mail.'); return; }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) { setError('Digite um WhatsApp válido com DDD.'); return; }
    if (form.password.length < 6) { setError('Senha deve ter ao menos 6 caracteres.'); return; }
    if (form.password !== form.confirm) { setError('As senhas não coincidem.'); return; }
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, form.email.trim(), form.password);
      const initials = form.name.trim().split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();
      localStorage.setItem('maw_profile', JSON.stringify({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: phoneDigits,
        initials,
      }));
      // Marcador de cadastro — dispara a notificação pro Eder na hora, sem
      // depender do primeiro sync de dados financeiros (que só acontece quando
      // a pessoa realmente começa a usar, podendo demorar dias).
      setDoc(doc(db, 'signups', cred.user.uid), {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: phoneDigits,
        createdAt: new Date().toISOString(),
      }).catch(() => {});
      // onAuthStateChanged in App.js handles the rest
    } catch (e) {
      setError(firebaseError(e.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo">
          <MawLogoLogin />
        </div>

        {tab !== 'reset' && (
        <div className="login-tabs">
          <button className={`login-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => { setTab('login'); setError(''); }}>
            Entrar
          </button>
          <button className={`login-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => { setTab('register'); setError(''); }}>
            Cadastrar
          </button>
        </div>
        )}

        {tab === 'reset' && (
          <div className="login-form">
            {resetSent ? (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <i className="fas fa-envelope-circle-check" style={{ fontSize: 32, color: '#1d9e75', marginBottom: 12, display: 'block' }}></i>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>E-mail enviado!</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
                  Enviamos um link pra <strong>{resetEmail.trim()}</strong> com instruções pra criar uma senha nova. Verifique também a caixa de spam.
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Recuperar senha</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 14 }}>
                  Digite o e-mail cadastrado — vamos te mandar um link pra criar uma senha nova.
                </div>
                <div className="form-group">
                  <label className="form-label">E-mail</label>
                  <input className="form-control" type="email" placeholder="seu@email.com" value={resetEmail} onChange={e => setResetEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendReset()} autoFocus />
                </div>
                {error && <div className="login-error"><i className="fas fa-circle-exclamation"></i> {error}</div>}
                <button className="login-btn" onClick={sendReset} disabled={loading}>
                  {loading ? <><i className="fas fa-circle-notch fa-spin"></i> Enviando...</> : <><i className="fas fa-paper-plane"></i> Enviar link de recuperação</>}
                </button>
              </>
            )}
            <div className="login-demo">
              <button className="login-demo-btn" onClick={() => { setTab('login'); setError(''); setResetSent(false); }}>
                <i className="fas fa-arrow-left"></i> Voltar para o login
              </button>
            </div>
          </div>
        )}

        {tab === 'login' && (
          <div className="login-form">
            <div className="form-group">
              <label className="form-label">E-mail</label>
              <input className="form-control" type="email" placeholder="seu@email.com" value={form.email} onChange={e => set('email', e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} />
            </div>
            <div className="form-group">
              <label className="form-label">Senha</label>
              <div style={{ position: 'relative' }}>
                <input className="form-control" type={showPass ? 'text' : 'password'} placeholder="••••••" value={form.password} onChange={e => set('password', e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} style={{ paddingRight: 42 }} />
                <button onClick={() => setShowPass(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14 }}>
                  <i className={`fas fa-eye${showPass ? '-slash' : ''}`}></i>
                </button>
              </div>
              <button
                onClick={() => { setTab('reset'); setError(''); setResetSent(false); setResetEmail(form.email); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 12, fontWeight: 600, marginTop: 6, padding: 0 }}
              >
                Esqueci minha senha
              </button>
            </div>

            {error && <div className="login-error"><i className="fas fa-circle-exclamation"></i> {error}</div>}

            <button className="login-btn" onClick={login} disabled={loading}>
              {loading ? <><i className="fas fa-circle-notch fa-spin"></i> Entrando...</> : <><i className="fas fa-right-to-bracket"></i> Entrar</>}
            </button>

            <div className="login-demo">
              <div className="login-demo-label">Não tem conta ainda?</div>
              <button className="login-demo-btn" onClick={() => { setTab('register'); setError(''); }}>
                <i className="fas fa-user-plus"></i> Criar conta gratuita
              </button>
            </div>

            <button
              onClick={() => { window.location.href = '/?demo=true'; }}
              style={{
                width: '100%', marginTop: 8, padding: '10px 16px',
                background: 'transparent', border: '1px solid rgba(var(--gold-rgb),0.4)',
                borderRadius: 10, cursor: 'pointer', color: 'var(--gold)',
                fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8, transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--gold-rgb),0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <i className="fas fa-flask"></i> Ver demonstração
            </button>
          </div>
        )}

        {tab === 'register' && (
          <div className="login-form">
            <div className="form-group">
              <label className="form-label">Nome completo</label>
              <input className="form-control" type="text" placeholder="Seu nome completo" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">WhatsApp</label>
              <input className="form-control" type="tel" inputMode="numeric" placeholder="(11) 91234-5678" value={form.phone} onChange={e => set('phone', formatPhone(e.target.value))} />
            </div>
            <div className="form-group">
              <label className="form-label">E-mail</label>
              <input className="form-control" type="email" placeholder="seu@email.com" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Senha</label>
              <input className="form-control" type="password" placeholder="Mínimo 6 caracteres" value={form.password} onChange={e => set('password', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirmar senha</label>
              <input className="form-control" type="password" placeholder="Repita a senha" value={form.confirm} onChange={e => set('confirm', e.target.value)} onKeyDown={e => e.key === 'Enter' && register()} />
            </div>

            {error && <div className="login-error"><i className="fas fa-circle-exclamation"></i> {error}</div>}

            <button className="login-btn" onClick={register} disabled={loading}>
              {loading ? <><i className="fas fa-circle-notch fa-spin"></i> Criando conta...</> : <><i className="fas fa-user-plus"></i> Criar conta</>}
            </button>

            <div className="login-demo">
              <div className="login-demo-label">Já tem conta?</div>
              <button className="login-demo-btn" onClick={() => { setTab('login'); setError(''); }}>
                <i className="fas fa-right-to-bracket"></i> Fazer login
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="login-footer">MAW FINANÇAS © {new Date().getFullYear()}</div>
    </div>
  );
}
